import { Baseline, Highlighter, Pencil, Upload, X } from "lucide-react";
import { Post } from "../../types/post";
import { appendNewFiles, handleFilesChange, handleRemoveExistsMedia, handleRemoveFile, timeAgo } from "../../utils";
import { WORKER_URL } from "../../constants";
import { useForm } from "react-hook-form";
import { FormContent } from "../../App";
import { useEffect, useRef, useState } from "react";
import { useUpdatePost, useUploadMedias } from "../../api/contentApi";
import { useDropzone } from "react-dropzone";
import { TextEditor } from "../textEditor/TextEditor";
import { SwatchesPicker } from "../colorPicker/ColorPicker";
import "./ContentPost.scss";
import { Editor } from "@tiptap/react";
import { toast } from "react-toastify";

type Props = {
    post: Post,
    posts: Post[],
    editPostId: number | null,
    setEditPostId: React.Dispatch<React.SetStateAction<number | null>>,
    setPosts: React.Dispatch<React.SetStateAction<Post[]>>
}

export const ContentPost = ({ post, posts, editPostId, setEditPostId, setPosts }: Props) => {
    const linkRegex = /(https?:\/\/[^\s]+)/g;
    const htmlRegex = /<\/?[a-z][\s\S]*>/i;

    const [editorInstance, setEditorInstance] = useState<{
        getEditor: () => Editor | null;
    } | null>(null);

    const [textColor, setTextColor] = useState(post.headerTextColor);
    const [textBg, setTextBg] = useState(post.headerBg);

    const [selectTextColor, setSelectTextColor] = useState(false);
    const [selectBgColor, setSelectBgTextColor] = useState(false);
    const { mutate: updatePost, isPending: updatePostPending } = useUpdatePost();
    const { mutateAsync: uploadMedias, isPending: isMediasUploading } = useUploadMedias();

    const editFormAddMediaInput = useRef<HTMLInputElement>(null);

    const editForm = useForm<FormContent>({
        defaultValues: {
            mediaFiles: null
        }
    });

    const editFormData = editForm.watch();

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: {
            'image/*': [],
            'video/*': [],
            'audio/*': []
        },
        onDrop: (acceptedFiles: File[]) => {
            const existingFiles = Array.from(editForm.getValues("mediaFiles") ?? []);
            appendNewFiles(editForm, existingFiles, acceptedFiles);
        }
    });

    const handleOpenEditForm = async (item: Post) => {
        setEditPostId(item.id)

        editForm.reset({
            head: item.head,
            content: item.content,
            mediaKeys: item.mediaKeys
        })
    }

    const handleSubmitEdit = async (data: FormContent) => {
        try {
            const editor = editorInstance?.getEditor();
            if (editor) {
                const { mediaFiles, mediaKeys, head } = data;
                let newMediaKeys: string[] = []
                if (mediaFiles) await uploadMedias(mediaFiles, {
                    onSuccess: (keys) => {
                        newMediaKeys = keys
                    }
                });
                const postData = posts.find((item) => item.id === editPostId) as Post;
                const updatedPostBody = {
                    ...postData,
                    head,
                    content: editor.getHTML(),
                    actualMediaKeys: mediaKeys,
                    newMediaKeys,
                };
                updatePost(updatedPostBody, {
                    onSuccess: (updatedPost) => {
                        setPosts(prevPosts =>
                            prevPosts.map(post => post.id === updatedPostBody.id ? updatedPost : post)
                        );
                        setEditPostId(null);
                        editor.commands.clearContent();
                        toast("Post updated!", {
                            type: "success"
                        });
                    },
                });
            }

        }
        catch (err) {
            toast("Edit post error!", {
                type: "error"
            });
            console.error(err);
        }
    }

    function getPreset() {
        if (post.content && htmlRegex.test(post.content)) {
            return post.content
        }
        else if (post.content === null) return "";
        else return `<p>${post.content}</p>`;
    }

    useEffect(() => {
        if (editPostId) {
            const handlePaste = (e: ClipboardEvent) => {
                if (!e.clipboardData) return;
                const clipboardFiles = Array.from(e.clipboardData.files).filter((file) =>
                    file.type.startsWith('image/') || file.type.startsWith('video/') || file.type.startsWith('audio/')
                );
                const existingFiles = Array.from(editForm.getValues("mediaFiles") ?? []);
                appendNewFiles(editForm, existingFiles, clipboardFiles);
            };

            window.addEventListener('paste', handlePaste);
            return () => window.removeEventListener('paste', handlePaste);
        }

    }, [editPostId]);
    return (<>
        {editPostId === post.id ? <div className="edit-form">
            <form className="form" id="post-form" onSubmit={editForm.handleSubmit(handleSubmitEdit)}>
                <div className="form-inputs">
                    <div className="edit-form__head">
                        <input
                            className="form-input"
                            type="text"
                            {...editForm.register("head", { required: true })}
                            style={{ backgroundColor: `${textBg}`, color: `${textColor}` }}
                            placeholder="Title…"
                        />
                        <button
                            className="toolbar__color-button header-color-select"
                            onClick={() => {
                                setSelectTextColor(true);
                                setSelectBgTextColor(false);
                            }}
                            type="button">
                            <Baseline width={18} height={18} />
                            <div className="toolbar__current-color" style={{ backgroundColor: `${textColor}` }} />
                        </button>
                        <button
                            className="toolbar__color-button header-color-select"
                            onClick={() => {
                                setSelectBgTextColor(true);
                                setSelectTextColor(false);
                            }}
                            type="button">
                            <Highlighter width={18} height={18} />
                            <div className="toolbar__current-color" style={{ backgroundColor: `${textBg}` }} />
                        </button>
                        {selectBgColor && <div className="color-picker-container">
                            <SwatchesPicker
                                color={textBg}
                                onChange={setTextBg}
                                onClose={() => {
                                    setSelectBgTextColor(false);
                                }}
                                setDefault={() => setTextBg("#ffffff")}
                                textColor={textColor}
                                bgColor={textBg}
                            />
                        </div>
                        }
                        {selectTextColor && <div className="color-picker-container">
                            <SwatchesPicker
                                color={textColor}
                                onChange={setTextColor}
                                onClose={() => {
                                    setSelectTextColor(false);
                                }}
                                setDefault={() => setTextColor("#000000")}
                                textColor={textColor}
                                bgColor={textBg} />
                        </div>
                        }
                    </div>
                    <TextEditor preset={getPreset()} ref={setEditorInstance} />
                </div>
                <div className="add-media-area" {...getRootProps()}>
                    <div
                        className={`media-dnd-area ${isDragActive ? "drag-active" : ""}`}
                        onClick={() => {
                            editFormAddMediaInput.current?.click()
                        }}>
                        <div className="media-dnd-area-text">
                            <span>Upload medias from computer or drag and drop</span>
                        </div>
                        <Upload />
                    </div>
                </div>
                <input
                    className="edit-form-input"
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    {...getInputProps()}
                    {...editForm.register("mediaFiles")}
                    onChange={(e) => handleFilesChange(e, editForm)}
                    ref={(e) => {
                        editForm.register("mediaFiles").ref(e);
                        editFormAddMediaInput.current = e;
                    }}
                />

                <div className="preview-container">
                    {editFormData.mediaKeys && <>
                        {editFormData.mediaKeys.map((key, i) => {
                            return <div className="edit-image-container" key={i}>
                                <button
                                    type="button"
                                    className="remove-image"
                                    onClick={() => handleRemoveExistsMedia(key, editForm, editFormData)}>
                                    <X />
                                </button>
                                <img className="preview" src={WORKER_URL + `/posts/${key}`} />
                            </div>
                        })}
                        {editFormData.mediaFiles && <>
                            {Array.from(editFormData.mediaFiles).map((media) => {
                                return <div className="edit-image-container">
                                    <button
                                        type="button"
                                        className="remove-image"
                                        onClick={() => handleRemoveFile(media, editForm)}>
                                        <X />
                                    </button>
                                    <img className="preview" src={URL.createObjectURL(media)} />
                                </div>
                            })}
                        </>}
                    </>}
                </div>

                <div className="form-input-row">
                    <button className="save-button" type="submit" disabled={updatePostPending || isMediasUploading}>
                        Save
                    </button>
                    <button className="cancel-button" onClick={() => {
                        setEditPostId(null);
                        editForm.reset();
                    }}>
                        Cancel
                    </button>
                </div>
            </form>
        </div> : <div className="post">
            <div className="post-head">
                <h3 className="post-title" style={{
                    backgroundColor: post.headerBg,
                    color: post.headerTextColor
                }}>{post.head}</h3>
                <button className="edit-button" onClick={() => handleOpenEditForm(post)}>
                    <Pencil width={14} height={14} />
                </button>
            </div>
            {(post.content && htmlRegex.test(post.content)) ? <div className="post__content">
                {<div dangerouslySetInnerHTML={{ __html: post.content }} />}
            </div> : <div className="post__content" key={post.id}>
                {post.content && <div className="post-content">
                    {post.content.split(linkRegex).map((content, i) => {
                        if (content.match(linkRegex)) return <a key={i} href={content} target="_blank">{content}</a>
                        return content;
                    })}
                </div>}
            </div>}
            {post.mediaKeys && <div className="preview-container">
                {post.mediaKeys.map((key, i) => {
                    return <img className="preview" src={WORKER_URL + `/posts/${key}`} key={i} />
                })}
            </div>}
            <div className="post-time">
                {timeAgo(post.createdAt)}
            </div>
        </div>}
    </>)
}